import { asRecord } from '../loaders/utils'
import type { ImportedBrepRef } from '../loaders/types'
import { createSha256Hex } from '../../utils/hash/createSha256Hex'

/**
 * sidecar payload 字节长度校验编码器。
 */
const sidecarPayloadEncoder = new TextEncoder()

/** 已按真实 UTF-8 payload 计算并通过 SHA-256 校验的 entry。 */
const verifiedImportedBrepSidecarEntries = new WeakSet<ImportedBrepSidecarEntry>()

/**
 * sidecar 中单条 BRep payload 记录。
 */
export interface ImportedBrepSidecarEntry {
  /**
   * payload 编码。
   */
  encoding: string
  /**
   * payload 字节长度。
   */
  byteLength: number
  /**
   * payload 哈希。
   */
  sha256: string
  /**
   * ASCII BRep 文本。
   */
  payload: string
}

/**
 * sidecar 文档结构。
 */
export interface ImportedBrepSidecarDocument {
  /**
   * 按 key 建立的 entry 映射。
   */
  entries: Record<string, ImportedBrepSidecarEntry>
}

/**
 * 归一化导入资产文件 ID。
 *
 * 设计说明：
 * 1. 文档 6 明确 `uri` 是相对当前 GLB 的相对路径，因此这里统一折叠 `.`、`..` 与反斜杠；
 * 2. 返回值始终是项目内可比较的相对 `fileId`，便于与 `projectFiles[*].fileId` 做稳定命中；
 * 3. 不在这里引入任何 API 或磁盘路径概念，保持该工具只处理项目文件 ID。
 *
 * @param fileId 原始文件 ID。
 * @returns 归一化后的文件 ID。
 */
export function normalizeImportedAssetFileId(fileId: string): string {
  const segments = fileId
    .replace(/\\/g, '/')
    .split('/')
    .map((segment) => segment.trim())

  const normalizedSegments: string[] = []
  segments.forEach((segment) => {
    if (!segment || segment === '.') {
      return
    }

    if (segment === '..') {
      normalizedSegments.pop()
      return
    }

    normalizedSegments.push(segment)
  })

  return normalizedSegments.join('/')
}

/**
 * 解析 sidecar entry。
 *
 * @param value 原始 entry 值。
 * @returns 解析后的 entry；无效时返回 `undefined`。
 */
function parseImportedBrepSidecarEntry(value: unknown): ImportedBrepSidecarEntry | undefined {
  const record = asRecord(value)
  if (!record) {
    return undefined
  }

  const encoding = typeof record.encoding === 'string' ? record.encoding.trim() : ''
  const sha256 = typeof record.sha256 === 'string' ? record.sha256.trim() : ''
  const payload = typeof record.payload === 'string' ? record.payload : ''
  const byteLength = typeof record.byteLength === 'number' && Number.isFinite(record.byteLength)
    ? record.byteLength
    : NaN

  if (!encoding || !sha256 || payload.length === 0 || !Number.isFinite(byteLength) || byteLength <= 0) {
    return undefined
  }

  return {
    encoding,
    sha256,
    payload,
    byteLength,
  }
}

/**
 * 解析 sidecar JSON 文本。
 *
 * @param rawText sidecar 原文。
 * @returns 归一化后的 sidecar 文档。
 * @throws 当 JSON 无法解析、缺少 entries 字段或没有有效 entry 时抛出错误。
 */
export function parseImportedBrepSidecar(rawText: string): ImportedBrepSidecarDocument {
  let parsed: unknown

  try {
    parsed = JSON.parse(rawText)
  } catch (error) {
    throw new Error(`BRep sidecar JSON 解析失败: ${error instanceof Error ? error.message : String(error)}`)
  }

  const record = asRecord(parsed)
  const rawEntries = asRecord(record?.entries)
  if (!rawEntries) {
    throw new Error('BRep sidecar 缺少 entries 字段')
  }

  const entries: Record<string, ImportedBrepSidecarEntry> = {}
  Object.entries(rawEntries).forEach(([key, value]) => {
    const entry = parseImportedBrepSidecarEntry(value)
    if (!entry) {
      return
    }

    entries[key] = entry
  })

  if (Object.keys(entries).length === 0) {
    throw new Error('BRep sidecar 未解析出任何有效 entries')
  }

  return { entries }
}

/**
 * 命中指定 `brepRef` 对应的 sidecar entry。
 *
 * 设计说明：
 * 1. 严格按文档 5 的约定校验 `encoding / byteLength / sha256`；
 * 2. 额外校验 `payload` 自身字节长度，避免 sidecar 条目内容与 entry 元数据脱节；
 * 3. 真正恢复 BRep 之前先在这里做纯数据层校验，能把协议问题尽早暴露。
 *
 * @param document sidecar 文档。
 * @param brepRef BRep 引用。
 * @returns 命中的 sidecar entry。
 * @throws 当 entry 缺失，或其编码、字节长度、哈希及载荷长度与引用声明不一致时抛出错误。
 */
export async function resolveImportedBrepSidecarEntry(
  document: ImportedBrepSidecarDocument,
  brepRef: ImportedBrepRef,
): Promise<ImportedBrepSidecarEntry> {
  const entry = document.entries[brepRef.key]
  if (!entry) {
    throw new Error(`BRep sidecar 未找到 entry: ${brepRef.key}`)
  }

  if (entry.encoding !== brepRef.encoding) {
    throw new Error(`BRep sidecar entry 编码不匹配: ${brepRef.key}`)
  }

  if (typeof brepRef.byteLength === 'number' && entry.byteLength !== brepRef.byteLength) {
    throw new Error(`BRep sidecar entry 字节长度不匹配: ${brepRef.key}`)
  }

  if (entry.sha256 !== brepRef.sha256) {
    throw new Error(`BRep sidecar entry 哈希不匹配: ${brepRef.key}`)
  }

  const payloadByteLength = sidecarPayloadEncoder.encode(entry.payload).byteLength
  if (payloadByteLength !== entry.byteLength) {
    throw new Error(`BRep sidecar payload 字节长度与 entry 声明不一致: ${brepRef.key}`)
  }

  const payloadSha256 = await createSha256Hex(sidecarPayloadEncoder.encode(entry.payload))
  if (payloadSha256 !== entry.sha256) {
    throw new Error(
      `BRep sidecar payload SHA-256 与 entry 声明不一致: ${brepRef.key}; expected=${entry.sha256}; actual=${payloadSha256}`,
    )
  }

  verifiedImportedBrepSidecarEntries.add(entry)

  return entry
}

/**
 * 判断 sidecar entry 是否已经根据真实 payload 字节通过 SHA-256 校验。
 *
 * 运行时缓存会在文档解析后提前建立候选索引，但只有该函数返回 `true` 的 entry 才能作为
 * 权威 payload 对外复用，避免两份相同声明哈希绕过内容校验。
 *
 * @param entry 待检查的 sidecar entry。
 * @returns 已通过内容摘要校验时返回 `true`。
 */
export function isImportedBrepSidecarEntryPayloadVerified(
  entry: ImportedBrepSidecarEntry | undefined,
): entry is ImportedBrepSidecarEntry {
  return Boolean(entry && verifiedImportedBrepSidecarEntries.has(entry))
}

/**
 * 解析 sidecar 文件 ID。
 *
 * 设计说明：
 * 1. 优先严格遵循文档 3.1/6，从 `brepRef.uri` 解析 sidecar 路径；
 * 2. 仅当历史数据缺失 `uri` 时，才回退到 `xxx.glb -> xxx.brep.sidecar.json`；
 * 3. `uri` 始终按“相对当前 GLB 文件”的规则拼接，而不是相对进程工作目录。
 *
 * @param glbFileId 当前 GLB 文件 ID。
 * @param brepRef BRep 引用。
 * @returns sidecar 文件 ID。
 */
export function resolveImportedBrepSidecarFileId(glbFileId: string, brepRef: ImportedBrepRef): string {
  const normalizedGlbFileId = normalizeImportedAssetFileId(glbFileId)
  const explicitUri = brepRef.uri.trim()

  if (explicitUri.length > 0) {
    if (explicitUri.startsWith('/')) {
      return normalizeImportedAssetFileId(explicitUri)
    }

    const segments = normalizedGlbFileId.split('/').filter(Boolean)
    segments.pop()
    const baseDirectory = segments.join('/')
    return normalizeImportedAssetFileId(
      baseDirectory.length > 0 ? `${baseDirectory}/${explicitUri}` : explicitUri,
    )
  }

  return normalizeImportedAssetFileId(
    normalizedGlbFileId.replace(/\.glb$/i, '.brep.sidecar.json'),
  )
}
